import { useEffect, useState, useContext, Fragment } from "react";
import { BuildingContext } from "Context/BuildingProvider";
import { GET_FLOOR_OVERLAYS } from "lib/buildings";
import { GetFloorOverlaysQueryResult } from "generated/graphql";
import { useQuery } from "@apollo/client";
import { OnlineBuildingGuard } from "lib/realtimeDBTypes";
import {
  ContentContainer,
  StandardContainer,
} from "components/Layout/StandardStyledComponents";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import {
  useMediaQuery,
  Drawer,
  Divider,
  IconButton,
  Typography,
  LinearProgress,
} from "@material-ui/core";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import { AuthContext } from "Firebase/AuthProvider";
import StandardModal from "components/Layout/StandardModal";
import useInterval from "components/Utils/UseInterval";
import Page from "components/Page";
import {
  OnlineUsersTable,
  DataUsagePrompt,
  TaskSender,
  MobileNavigation,
  VanillaMapContainer,
  PendingTasksTable,
  UserTasksTable,
  IncidentsTable,
  ChatContainer,
} from "Modules/LiveScreen";

interface LiveScreenState {
  onlineBuildingGuards: OnlineBuildingGuard[];
  uiValue: number;
  userWatching: boolean;
  remindPromptInterval: number;
  onlineBuildingGuardIds: string[];
  selectMinutesModalOpen: boolean;
  currentMinutes: number;
  sendToType: string;
  user?: string;
}

const DEFAULT_STATE: LiveScreenState = {
  onlineBuildingGuards: [],
  uiValue: 3,
  userWatching: false,
  remindPromptInterval: 5,
  onlineBuildingGuardIds: [],
  selectMinutesModalOpen: true,
  currentMinutes: 0,
  sendToType: "Role",
};

const drawerWidth = 240;

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
  },
  appBar: {
    transition: theme.transitions.create(["margin", "width"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
  },
  appBarShift: {
    width: `calc(100% - ${drawerWidth}px)`,
    marginLeft: drawerWidth,
    transition: theme.transitions.create(["margin", "width"], {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
  },
  menuButton: {
    marginRight: theme.spacing(2),
  },
  hide: {
    display: "none",
  },
  drawer: {
    width: "40vw",
    flexShrink: 0,
  },
  drawerPaper: {
    width: "40vw",
  },
  drawerMobile: {
    width: "110vw",
    flexShrink: 0,
  },
  drawerPaperMobile: {
    width: "100vw",
  },
  paper: {
    position: "absolute",
    width: 400,
    backgroundColor: theme.palette.background.paper,
    border: "2px solid #000",
    boxShadow: theme.shadows[5],
    padding: theme.spacing(2, 4, 3),
  },
  drawerHeader: {
    display: "flex",
    alignItems: "center",
    padding: theme.spacing(0, 1),
    // necessary for content to be below app bar
    ...theme.mixins.toolbar,
    justifyContent: "flex-start",
  },
  content: {
    flexGrow: 1,
    padding: theme.spacing(3),
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    marginLeft: -drawerWidth,
  },
  contentShift: {
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginLeft: 0,
  },
}));

export default function LiveScreen() {
  const { currentBuilding, buildingState, updateBuilding } =
    useContext(BuildingContext);
  const { getDatabase, setRoute } = useContext(AuthContext);
  const classes = useStyles();
  const theme = useTheme();
  let interValFunction = null as any;
  let timeOut;

  const [state, setState] = useState(DEFAULT_STATE);
  const isDesktop = useMediaQuery("(min-width:600px)");

  const { loading, error, data }: GetFloorOverlaysQueryResult = useQuery(
    GET_FLOOR_OVERLAYS,
    {
      variables: {
        buildingId: currentBuilding?.Id,
      },
    }
  );

  useEffect(() => {
    setRoute("Live Screen");
    //initFirebase();
  }, []);

  const setButtonValue = (value: number): void => {
    const openModal = value == 3;
    if (state.uiValue == value) {
      setState({ ...state, uiValue: 10 });
    } else {
      setState({ ...state, uiValue: value, selectMinutesModalOpen: openModal });
    }
  };

  const handleDrawerClose = (): void => {
    setState({ ...state, uiValue: 10 });
  };

  const handleMinutesChange = (minutes: number) => {
    setState({ ...state, remindPromptInterval: minutes });
  };

  const handleStartWatching = () => {
    postUserStartedWatching();
    setState({
      ...state,
      userWatching: true,
      selectMinutesModalOpen: false,
      currentMinutes: 0,
    });
  };

  useInterval(() => {
    if (state.userWatching) {
      if (state.currentMinutes < state.remindPromptInterval) {
        postUserStartedWatching();
        setState({ ...state, currentMinutes: state.currentMinutes + 1 });
      } else {
        setState({
          ...state,
          userWatching: false,
          currentMinutes: 0,
          selectMinutesModalOpen: true,
        });
      }
    }
  }, 60000);
  const postUserStartedWatching = () => {
    if (!!getDatabase) {
      const database = getDatabase();
      var today = new Date();
      var date =
        today.getFullYear() +
        "-" +
        (today.getMonth() + 1) +
        "-" +
        today.getDate();
      var timestamp = today.getTime();
      var time =
        today.getHours() + ":" + today.getMinutes() + ":" + today.getSeconds();
      database.ref("buildings/" + currentBuilding!.Id + "/lastWatchTime").set({
        timestamp,
      });
    }
  };

  const setIntervalAndStartPosting = () => {
    postUserStartedWatching();
    setState({ ...state, uiValue: 10, userWatching: true });
    interValFunction = setInterval(
      postUserStartedWatching,
      60000,
      currentBuilding!.Id
    );
    const timeDelay = state.remindPromptInterval * 60000;
    timeOut = setTimeout(() => {
      setState({ ...state, userWatching: false, uiValue: 3 });
      clearInterval(interValFunction);
    }, timeDelay);
  };

  const handleModalClose = () => {
    setState({ ...state, uiValue: 10 });
  };

  const handleSendToTypeChange = (event: any) => {
    setState({ ...state, sendToType: event.target.value });
  };

  const handleUserChange = (user: string) => {
    console.log(
      "🚀 ~ file: liveScreen.tsx ~ line 238 ~ handleUserChange ~ user",
      user
    );
    setState({ ...state, user });
  };

  const sendTaskToUser = (
    taskId: number,
    buildingGuardId: number,
    taskName: string,
    priority: number
  ) => {
    console.log(taskId, buildingGuardId, taskName, priority);
    console.log(state);
    if (!!getDatabase) {
      const database = getDatabase();
      var today = new Date();
      var date =
        today.getFullYear() +
        "-" +
        (today.getMonth() + 1) +
        "-" +
        today.getDate();
      var timestamp = today.getTime();
      var time =
        today.getHours() + ":" + today.getMinutes() + ":" + today.getSeconds();
      var dateTime = date + " " + time;
      const userName =
        buildingState?.onlineBuildingGuards[buildingGuardId]?.name;
      database
        .ref(
          "buildings/" + currentBuilding?.Id + "/userTasks/" + buildingGuardId
        )
        .set({
          taskId: taskId,
          buildingGuardId: buildingGuardId,
          priority,
          timeString: dateTime,
          timestamp: timestamp,
          taskName,
          userName,
        });
      setState({ ...state, uiValue: 0 });
    }
  };

  const handleMarkerClicked = (user: string) => {
    setState({ ...state, user });
  };

  const sendTaskToRole = (
    taskId: number,
    roleId: string | number | undefined,
    taskName: string,
    priority: number
  ) => {
    let role;
    if (typeof roleId === "number") {
      role = "" + roleId;
    } else {
      role = roleId;
    }
    if (!!getDatabase) {
      const database = getDatabase();
      var today = new Date();
      var date =
        today.getFullYear() +
        "-" +
        (today.getMonth() + 1) +
        "-" +
        today.getDate();
      var timestamp = today.getTime();
      var time =
        today.getHours() + ":" + today.getMinutes() + ":" + today.getSeconds();
      var dateTime = date + " " + time;
      database
        .ref("buildings/" + currentBuilding?.Id + "/pendingTasks/" + taskId)
        .set({
          id: taskId,
          roleId: role,
          priority: priority,
          timeString: dateTime,
          timestamp: timestamp,
          taskName: taskName,
        });
      setState({ ...state, uiValue: 0 });
    }
  };

  if (loading) {
    return <LinearProgress />;
  } else if (error) {
    return <div>{error.message}</div>;
  } else if (data?.FloorOverlay) {
    let navigationPanel;
    let uiElement;

    navigationPanel = (
      <MobileNavigation setButtonValue={setButtonValue}></MobileNavigation>
    );
    let desktopElement;
    let drawerTitle = "";
    switch (state.uiValue) {
      case 0:
        drawerTitle = "Info Window";
        desktopElement = (
          <Fragment>
            <OnlineUsersTable
              onlineBuildingGuards={
                buildingState?.onlineBuildingGuards
                  ? buildingState?.onlineBuildingGuards
                  : []
              }
            />
            <PendingTasksTable />
            <UserTasksTable />
          </Fragment>
        );
        break;
      case 1:
        drawerTitle = "Chat";
        desktopElement = <ChatContainer />;
        break;
      case 2:
        drawerTitle = "Send Task";
        desktopElement = (
          <TaskSender
            sendToType={state.sendToType}
            onlineBuildingGuards={
              buildingState?.onlineBuildingGuards
                ? buildingState?.onlineBuildingGuards
                : []
            }
            handleSendToTypeChange={handleSendToTypeChange}
            handleUserChange={handleUserChange}
            user={state.user}
            sendTaskToUser={sendTaskToUser}
            sendTaskToRole={sendTaskToRole}
          />
        );
        break;
      case 3:
        desktopElement = <div>data</div>;
        break;
      case 4:
        drawerTitle = "Incidents";
        desktopElement = <IncidentsTable />;
    }
    if (state.uiValue !== 3) {
      uiElement = (
        <Drawer
          className={isDesktop ? classes.drawer : classes.drawerMobile}
          variant="persistent"
          anchor="right"
          open={state.uiValue !== 10}
          classes={{
            paper: isDesktop ? classes.drawerPaper : classes.drawerPaperMobile,
          }}
        >
          <div className={classes.drawerHeader}>
            <Typography color="textPrimary" variant="h5">
              {drawerTitle}
            </Typography>
            <IconButton onClick={handleDrawerClose}>
              {theme.direction === "rtl" ? (
                <ChevronLeftIcon />
              ) : (
                <ChevronRightIcon />
              )}
            </IconButton>
          </div>
          <Divider />
          {desktopElement}
        </Drawer>
      );
    } else if (!!desktopElement) {
      const dataMinutesForm = (
        <DataUsagePrompt
          minutes={state.remindPromptInterval}
          handleMinutesChange={handleMinutesChange}
        />
      );
      let newUivalue = 10;
      if (state.uiValue !== 3) {
        newUivalue = state.uiValue;
      }
      uiElement = (
        <StandardModal
          open={state.selectMinutesModalOpen}
          title="Make Users Post Location Using Mobile Data?"
          onCancel={() => {
            setState({
              ...state,
              userWatching: false,
              selectMinutesModalOpen: false,
              uiValue: newUivalue,
            });
          }}
          onSubmit={handleStartWatching}
          children={dataMinutesForm}
        />
      );
    }

    return (
      <Page>
        <StandardContainer>
          <ContentContainer>
            <VanillaMapContainer
              handleMarkerClicked={handleMarkerClicked}
              onlineBuildingGuards={
                buildingState?.onlineBuildingGuards
                  ? buildingState?.onlineBuildingGuards
                  : []
              }
              floorOverlays={data.FloorOverlay}
              onlineBuildingGuardIds={state.onlineBuildingGuardIds}
            ></VanillaMapContainer>
            {navigationPanel}
          </ContentContainer>
          {uiElement}
        </StandardContainer>
      </Page>
    );
  } else {
    return <div>No Floor Plans</div>;
  }
}
